import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { dewarService } from '../services/dewarServiceAppwrite';
import { sampleService } from '../services/sampleServiceAppwrite';
import { performanceMonitor } from '../utils/performanceMonitor';
import Navbar from '../components/Navbar';

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [dewars, setDewars] = useState([]);
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // OPTIMIZATION: Track component performance
  useEffect(() => {
    performanceMonitor?.logComponentRender('Dashboard');
  }, []);

  // OPTIMIZATION: Memoize calculations to avoid recalculating on every render
  const stats = useMemo(() => {
    if (!dewars || !samples) {
      return {
        totalDewars: 0,
        totalSamples: 0,
        operationalDewars: 0
      };
    }

    return {
      totalDewars: dewars?.length || 0,
      totalSamples: samples?.length || 0,
      operationalDewars: dewars?.filter(d => d?.status === 'Operational')?.length || 0
    };
  }, [dewars, samples]);

  // FIX: Wrap loadData in useCallback to prevent infinite loops
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // OPTIMIZATION: Use performance monitoring
      await performanceMonitor?.measureAsync('Dashboard Data Load', async () => {
        const [dewarsData, samplesData] = await Promise.all([
          dewarService?.getAll(),
          sampleService?.getAll()
        ]);

        setDewars(dewarsData);
        setSamples(samplesData);
      });
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  }, []); // Empty dependency array - loadData logic doesn't depend on external values

  useEffect(() => {
    // Initial data load
    loadData();

    // APPWRITE: Realtime subscriptions using service layer
    // The dewarServiceAppwrite and sampleServiceAppwrite handle Appwrite realtime internally
    const unsubscribeDewars = dewarService?.subscribe((dewarsData) => {
      setDewars(dewarsData);
    });

    const unsubscribeSamples = sampleService?.subscribe((samplesData) => {
      setSamples(samplesData);
    });

    // OPTIMIZATION: Fallback polling with longer duration (30s)
    // This ensures data stays fresh even if realtime subscriptions fail
    const interval = setInterval(loadData, 30000);

    return () => {
      clearInterval(interval);
      if (unsubscribeDewars) unsubscribeDewars();
      if (unsubscribeSamples) unsubscribeSamples();
    };
  }, [loadData]); // Now safe to include loadData as dependency

  // OPTIMIZATION: Memoize formatTimeAgo function to prevent recreation on every render
  const formatTimeAgo = useCallback((dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now - date;
    const diffInHours = Math?.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math?.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays > 0) {
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    } else if (diffInHours > 0) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    } else {
      return 'Recently';
    }
  }, []);

  // OPTIMIZATION: Memoize static data arrays to prevent recreation
  const statsDisplay = useMemo(() => [
    { label: 'Total Dewars', value: stats?.totalDewars?.toString(), color: 'bg-blue-500', icon: '❄️' },
    { label: 'Total Samples', value: stats?.totalSamples?.toString(), color: 'bg-green-500', icon: '🧪' }
  ], [stats]);

  const quickActions = useMemo(() => [
    { title: 'View Dewars', path: '/dewars', icon: '❄️' },
    { title: 'Generate Report', path: '/reports', icon: '📊' },
    { title: 'Manage Users', path: '/users', icon: '👥' }
  ], []);

  const tabs = useMemo(() => [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'dewars', label: 'Dewars', icon: '❄️' },
    { id: 'samples', label: 'Samples', icon: '🧪' },
    { id: 'nitrogen', label: 'Nitrogen Levels', icon: '💧' },
    { id: 'activity', label: 'Recent Activity', icon: '🕒' },
    { id: 'settings', label: 'Settings', icon: '⚙️' },
    { id: 'actions', label: 'Quick Actions', icon: '⚡' }
  ], []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-2 text-gray-600">Overview of your cryogenic storage system</p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-6">
            <p className="font-medium">Error loading dashboard data</p>
            <p className="text-sm mt-1">{error}</p>
            <button
              onClick={loadData}
              className="mt-2 text-sm underline hover:no-underline"
            >
              Try again
            </button>
          </div>
        ) : (
          <>
            {/* OPTIMIZATION: Use memoized stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-sm font-medium text-gray-600">Total Dewars</h3>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats?.totalDewars || 0}</p>
                <p className="text-sm text-gray-500 mt-1">
                  {stats?.operationalDewars || 0} operational
                </p>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-sm font-medium text-gray-600">Total Samples</h3>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats?.totalSamples || 0}</p>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-sm font-medium text-gray-600">Operational Dewars</h3>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats?.operationalDewars || 0}</p>
                <p className="text-sm text-gray-500 mt-1">of {stats?.totalDewars || 0} total</p>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="bg-white rounded-lg shadow-md mb-6 sm:mb-8">
              <div className="border-b border-gray-200">
                <nav className="flex flex-wrap -mb-px">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`
                        flex items-center gap-2 px-4 sm:px-6 py-3 sm:py-4 text-sm sm:text-base font-medium border-b-2 transition-colors
                        ${activeTab === tab?.id
                          ? 'border-indigo-600 text-indigo-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }
                      `}
                    >
                      <span className="text-lg sm:text-xl">{tab?.icon}</span>
                      <span className="hidden sm:inline">{tab?.label}</span>
                    </button>
                  ))}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="p-4 sm:p-6">
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
                      {statsDisplay?.map((stat, index) => (
                        <div key={index} className="bg-gray-50 rounded-lg shadow-sm p-4 sm:p-6">
                          <div className={`${stat?.color} w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center mb-3 sm:mb-4`}>
                            <span className="text-lg sm:text-xl">{stat?.icon}</span>
                          </div>
                          <p className="text-xs sm:text-sm text-gray-600">{stat?.label}</p>
                          <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">{stat?.value}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Nitrogen Levels Tab */}
                {activeTab === 'nitrogen' && (
                  <div>
                    <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">Liquid Nitrogen Levels</h2>
                    {dewars?.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {dewars?.map((dewar) => {
                          const level = dewar?.liquidNitrogenLevel || 14;
                          const percentage = (level / 22) * 100;
                          const levelStatus = level <= 4 ? 'Critical' : level <= 8 ? 'Low' : 'Good';
                          const levelColor = level <= 4 ? 'bg-red-500' : level <= 8 ? 'bg-yellow-500' : 'bg-green-500';
                          const borderColor = level <= 4 ? 'border-red-200' : level <= 8 ? 'border-yellow-200' : 'border-green-200';
                          const bgGradient = level <= 4
                            ? 'from-red-50 to-red-100'
                            : level <= 8
                            ? 'from-yellow-50 to-yellow-100'
                            : 'from-green-50 to-green-100';

                          return (
                            <div
                              key={dewar?.id}
                              className={`bg-gradient-to-br ${bgGradient} p-4 rounded-lg border-2 ${borderColor} transition-all hover:shadow-lg`}
                            >
                              <div className="flex justify-between items-start mb-3">
                                <div>
                                  <h3 className="font-bold text-gray-900">{dewar?.name}</h3>
                                  {dewar?.location && (
                                    <p className="text-xs text-gray-600">{dewar?.location}</p>
                                  )}
                                </div>
                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                  level <= 4
                                    ? 'bg-red-200 text-red-800'
                                    : level <= 8
                                    ? 'bg-yellow-200 text-yellow-800'
                                    : 'bg-green-200 text-green-800'
                                }`}>
                                  {levelStatus}
                                </span>
                              </div>

                              <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                  <span className="text-sm text-gray-700">Level:</span>
                                  <span className="text-lg font-bold text-gray-900">{level}&quot; / 22&quot;</span>
                                </div>

                                <div className="w-full bg-gray-200 rounded-full h-4 relative overflow-hidden">
                                  <div
                                    className={`${levelColor} h-4 rounded-full transition-all duration-500 relative`}
                                    style={{ width: `${percentage}%` }}
                                  >
                                    <div className="absolute inset-0 bg-white/20 animate-pulse" />
                                  </div>
                                </div>

                                <div className="flex justify-between text-xs text-gray-600">
                                  <span>Empty</span>
                                  <span className="font-medium">{Math.round(percentage)}%</span>
                                  <span>Full</span>
                                </div>

                                {level <= 8 && (
                                  <div className={`mt-2 p-2 rounded text-xs font-medium ${
                                    level <= 4
                                      ? 'bg-red-100 text-red-800'
                                      : 'bg-yellow-100 text-yellow-800'
                                  }`}>
                                    {level <= 4
                                      ? '⚠️ Critical: Immediate refill required'
                                      : '⚡ Low: Refill recommended soon'
                                    }
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-gray-500">No dewars available. Add a dewar to monitor nitrogen levels.</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Dewars Tab */}
                {activeTab === 'dewars' && (
                  <div>
                    <div className="flex justify-between items-center mb-4 sm:mb-6">
                      <h2 className="text-lg sm:text-xl font-bold text-gray-900">Dewars Overview</h2>
                      <Link
                        to="/dewars"
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
                      >
                        Manage Dewars
                      </Link>
                    </div>
                    {dewars?.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {dewars?.map((dewar) => (
                          <div
                            key={dewar?.id}
                            className="bg-white border-2 border-gray-200 p-4 rounded-lg hover:shadow-lg transition-all"
                          >
                            <div className="flex justify-between items-start mb-3">
                              <div>
                                <h3 className="font-bold text-gray-900 text-lg">{dewar?.name}</h3>
                                {dewar?.location && (
                                  <p className="text-sm text-gray-600">{dewar?.location}</p>
                                )}
                              </div>
                              <span className="text-2xl">❄️</span>
                            </div>

                            <div className="space-y-2 mt-4">
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Total Racks:</span>
                                <span className="font-semibold">{dewar?.racks?.length || 0}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Total Boxes:</span>
                                <span className="font-semibold">
                                  {dewar?.racks?.reduce((total, rack) => total + (rack?.boxes?.length || 0), 0) || 0}
                                </span>
                              </div>

                              {/* Horizontal LN2 Level Gauge */}
                              <div className="pt-2 border-t border-gray-100">
                                <div className="flex justify-between items-center mb-1">
                                  <span className="text-sm text-gray-600">N₂ Level:</span>
                                  <span className={`text-sm font-bold ${
                                    (dewar?.liquidNitrogenLevel || 14) <= 4
                                      ? 'text-red-600'
                                      : (dewar?.liquidNitrogenLevel || 14) <= 8
                                      ? 'text-yellow-600'
                                      : 'text-green-600'
                                  }`}>
                                    {dewar?.liquidNitrogenLevel || 14}" / 22"
                                  </span>
                                </div>
                                <div className="relative w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full rounded-full transition-all duration-300 ${
                                      (dewar?.liquidNitrogenLevel || 14) <= 4
                                        ? 'bg-red-500'
                                        : (dewar?.liquidNitrogenLevel || 14) <= 8
                                        ? 'bg-yellow-500'
                                        : 'bg-green-500'
                                    }`}
                                    style={{
                                      width: `${Math.min(100, ((dewar?.liquidNitrogenLevel || 14) / 22) * 100)}%`
                                    }}
                                  />
                                </div>
                              </div>
                            </div>

                            <Link
                              to={`/dewars?view=${dewar?.id}`}
                              className="mt-4 block w-full text-center px-4 py-2 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors text-sm font-medium"
                            >
                              View Details
                            </Link>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 bg-gray-50 rounded-lg">
                        <span className="text-6xl mb-4 block">❄️</span>
                        <p className="text-gray-500 mb-4">No dewars configured yet</p>
                        <Link
                          to="/dewars"
                          className="inline-block px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                        >
                          Add Your First Dewar
                        </Link>
                      </div>
                    )}
                  </div>
                )}

                {/* Samples Tab */}
                {activeTab === 'samples' && (
                  <div>
                    <div className="flex justify-between items-center mb-4 sm:mb-6">
                      <h2 className="text-lg sm:text-xl font-bold text-gray-900">Samples Overview</h2>
                      <Link
                        to="/samples"
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
                      >
                        View All Samples
                      </Link>
                    </div>

                    <div className="grid grid-cols-1 gap-4 mb-6">
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-lg border-2 border-blue-200">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-blue-600 font-semibold">Total Samples</span>
                          <span className="text-3xl">🧪</span>
                        </div>
                        <p className="text-3xl font-bold text-blue-900">{stats?.totalSamples}</p>
                      </div>
                    </div>

                    {samples?.length > 0 ? (
                      <div>
                        <h3 className="text-md font-bold text-gray-900 mb-3">Recent Samples</h3>
                        <div className="space-y-3">
                          {samples?.slice(0, 5)?.map((sample, index) => (
                            <div key={index} className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                                  <span className="text-indigo-600 font-bold">🧪</span>
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">{sample?.sampleName}</p>
                                  <p className="text-sm text-gray-600">
                                    {sample?.dewarName} • {sample?.boxName} • Pos {sample?.position}
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-sm text-gray-500">{formatTimeAgo(sample?.freezeDate)}</p>
                                <p className="text-xs text-gray-400">{sample?.freezeDate}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 bg-gray-50 rounded-lg">
                        <span className="text-6xl mb-4 block">🧪</span>
                        <p className="text-gray-500 mb-4">No samples found</p>
                        <Link
                          to="/dewars"
                          className="inline-block px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                        >
                          Add Samples to Dewars
                        </Link>
                      </div>
                    )}
                  </div>
                )}

                {/* Settings Tab */}
                {activeTab === 'settings' && (
                  <div>
                    <div className="flex justify-between items-center mb-4 sm:mb-6">
                      <h2 className="text-lg sm:text-xl font-bold text-gray-900">Settings</h2>
                      <Link
                        to="/settings"
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
                      >
                        Full Settings
                      </Link>
                    </div>

                    <div className="space-y-4">
                      <div className="bg-white border-2 border-gray-200 p-6 rounded-lg">
                        <div className="flex items-center gap-3 mb-4">
                          <span className="text-3xl">👤</span>
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">Account Settings</h3>
                            <p className="text-sm text-gray-600">Manage your profile and preferences</p>
                          </div>
                        </div>
                        <Link
                          to="/settings?tab=account"
                          className="inline-block px-4 py-2 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors text-sm font-medium"
                        >
                          Configure
                        </Link>
                      </div>

                      <div className="bg-white border-2 border-gray-200 p-6 rounded-lg">
                        <div className="flex items-center gap-3 mb-4">
                          <span className="text-3xl">🔔</span>
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">Notifications</h3>
                            <p className="text-sm text-gray-600">Set up alerts for nitrogen levels and storage</p>
                          </div>
                        </div>
                        <Link
                          to="/settings?tab=notifications"
                          className="inline-block px-4 py-2 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors text-sm font-medium"
                        >
                          Configure
                        </Link>
                      </div>

                      <div className="bg-white border-2 border-gray-200 p-6 rounded-lg">
                        <div className="flex items-center gap-3 mb-4">
                          <span className="text-3xl">🔒</span>
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">Security</h3>
                            <p className="text-sm text-gray-600">Password and authentication settings</p>
                          </div>
                        </div>
                        <Link
                          to="/settings?tab=security"
                          className="inline-block px-4 py-2 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors text-sm font-medium"
                        >
                          Configure
                        </Link>
                      </div>

                      <div className="bg-white border-2 border-gray-200 p-6 rounded-lg">
                        <div className="flex items-center gap-3 mb-4">
                          <span className="text-3xl">⚙️</span>
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">System Preferences</h3>
                            <p className="text-sm text-gray-600">Application settings and data management</p>
                          </div>
                        </div>
                        <Link
                          to="/settings?tab=system"
                          className="inline-block px-4 py-2 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors text-sm font-medium"
                        >
                          Configure
                        </Link>
                      </div>
                    </div>
                  </div>
                )}

                {/* Recent Activity Tab */}
                {activeTab === 'activity' && (
                  <div>
                    <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">Recent Activity</h2>
                    {samples?.length > 0 ? (
                      <div className="space-y-3 sm:space-y-4">
                        {samples?.slice(0, 10)?.map((sample, index) => (
                          <div key={index} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 bg-gray-50 rounded-lg">
                            <div className="mb-2 sm:mb-0">
                              <p className="text-sm sm:text-base font-medium text-gray-900">{sample?.sampleName} added</p>
                              <p className="text-xs sm:text-sm text-gray-600">
                                {sample?.dewarName} • Box {sample?.boxName} • Position {sample?.position}
                              </p>
                            </div>
                            <span className="text-xs sm:text-sm text-gray-500">{formatTimeAgo(sample?.freezeDate)}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-gray-500">No recent activity. Start by adding samples to dewars.</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Quick Actions Tab */}
                {activeTab === 'actions' && (
                  <div>
                    <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">Quick Actions</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                      {quickActions?.map((action, index) => (
                        <Link
                          key={index}
                          to={action?.path}
                          className="flex flex-col items-center justify-center p-4 sm:p-6 bg-gray-50 rounded-lg hover:bg-indigo-50 transition-colors"
                        >
                          <span className="text-3xl sm:text-4xl mb-2 sm:mb-3">{action?.icon}</span>
                          <span className="text-xs sm:text-sm font-medium text-gray-900 text-center">{action?.title}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

// OPTIMIZATION: Wrap with React.memo to prevent unnecessary re-renders
export default React.memo(Dashboard);
